/**
 * `@quadratic/editor/schema` (architecture §11.1): the ProseMirror schema from the extension list via `getSchema`,
 * identical in the browser and in Node (no DOM needed; verified by test/schema.test.ts), plus the strict per-node and
 * per-mark attribute schemas `validateDoc` step 3 applies (unknown attributes are rejected, not dropped).
 */
import { getSchema } from '@tiptap/core';
import type { Schema } from '@tiptap/pm/model';
import { z } from 'zod';
import { CODE_LANGUAGES, MENTION_KINDS, schemaExtensions } from './extensions';

let cached: Schema | null = null;

export function editorSchema(): Schema {
  return (cached ??= getSchema(schemaExtensions()));
}

const zId = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/);
const none = z.object({}).strict();

/** Attributes per node type (as TipTap's `toJSON` writes them). */
export const NODE_ATTRS: Readonly<Record<string, z.ZodType>> = {
  doc: none,
  paragraph: none,
  text: none,
  hardBreak: none,
  horizontalRule: none,
  blockquote: none,
  bulletList: none,
  listItem: none,
  taskList: none,
  heading: z.object({ level: z.union([z.literal(1), z.literal(2), z.literal(3)]) }).strict(),
  orderedList: z.object({ start: z.number().int().min(0).max(100_000).optional(), type: z.null().optional() }).strict(),
  taskItem: z.object({ checked: z.boolean().optional() }).strict(),
  codeBlock: z.object({ language: z.enum(CODE_LANGUAGES).nullable().optional() }).strict(),
  mention: z.object({ kind: z.enum(MENTION_KINDS), id: zId, inert: z.boolean().optional() }).strict(),
  image: z.object({ fileId: zId }).strict(),
};

/** Attributes per mark type. Link `target`/`rel` are render-time constants; stored values may only be the defaults. */
export const MARK_ATTRS: Readonly<Record<string, z.ZodType>> = {
  bold: none,
  italic: none,
  strike: none,
  code: none,
  link: z
    .object({
      href: z.string().min(1).max(2048),
      target: z.literal('_blank').nullable().optional(),
      rel: z.literal('noopener noreferrer nofollow').nullable().optional(),
      class: z.null().optional(),
      title: z.string().max(200).nullable().optional(),
    })
    .strict(),
};

export { CODE_LANGUAGES, MENTION_KINDS, schemaExtensions, type MentionKind } from './extensions';
